import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type {
    RoadSection,
    RoadSectionId,
    RoadSectionSort,
    RoadSectionFilter,
    RoadSectionPagination,
} from '../domain/Reference';

export const moduleName = 'REFERENCE';

type State = {
    resetRoadSectionsOngoing: boolean;
    roadSections: Array<RoadSection>;
    selectedRoadSectionIds: Array<RoadSectionId>;
    sort: RoadSectionSort;
    filter: RoadSectionFilter;
    pagination: RoadSectionPagination;
    columnVisibility: Record<string, boolean>;
};

const initialState: State = {
    resetRoadSectionsOngoing: false,
    roadSections: [],
    selectedRoadSectionIds: [],
    sort: [],
    filter: { items: [] },
    pagination: { page: 0, pageSize: 100 },
    columnVisibility: {},
};

const referenceSlice = createSlice({
    name: moduleName,
    initialState,
    reducers: {
        resetStarted(state: State) {
            state.resetRoadSectionsOngoing = true;
            state.roadSections = [];
            state.selectedRoadSectionIds = [];
        },
        resetWith(
            state: State,
            action: PayloadAction<{
                roadSections: Array<RoadSection>;
                sort: RoadSectionSort;
                filter: RoadSectionFilter;
            }>,
        ) {
            state.sort = action.payload.sort;
            state.filter = action.payload.filter;
            state.roadSections = action.payload.roadSections;
            state.resetRoadSectionsOngoing = false;
            state.selectedRoadSectionIds = [];
        },
        setReference(state: State, action: PayloadAction<Array<RoadSection>>) {
            state.roadSections = action.payload;
        },
        selectRoadSection(state: State, action: PayloadAction<RoadSectionId>) {
            if (!state.selectedRoadSectionIds.includes(action.payload)) {
                state.selectedRoadSectionIds.push(action.payload);
            }
        },
        updatedRoadSection(state: State, action: PayloadAction<RoadSection>) {
            const idx = state.roadSections.findIndex((aroadSection) => aroadSection.id === action.payload.id);
            if (idx < 0) {
                throw new Error(`Road Section ${action.payload.id} not found in store`);
            }
            state.roadSections[idx] = action.payload;
        },
        updatedRoadSections(state: State, action: PayloadAction<Array<RoadSection>>) {
            const incomingById = new Map(action.payload.map((rs) => [rs.id, rs]));
            state.roadSections = state.roadSections.map((existing) => incomingById.get(existing.id) ?? existing);
        },
        deselectRoadSection(state: State, action: PayloadAction<RoadSectionId>) {
            state.selectedRoadSectionIds = state.selectedRoadSectionIds.filter((id) => id !== action.payload);
        },
        paginationUpdated(state: State, action: PayloadAction<RoadSectionPagination>) {
            state.pagination = { ...state.pagination, ...action.payload };
        },
        setSelectedRoadSections(state: State, action: PayloadAction<Array<RoadSectionId>>) {
            state.selectedRoadSectionIds = action.payload;
        },
        setColumnVisibility(state: State, action: PayloadAction<Record<string, boolean>>) {
            state.columnVisibility = action.payload;
        },
        filterUpdated(state: State, action: PayloadAction<RoadSectionFilter>) {
            state.filter = action.payload;
        },
    },
});

export const {
    resetStarted,
    resetWith,
    setReference,
    selectRoadSection,
    deselectRoadSection,
    setSelectedRoadSections,
    paginationUpdated,
    updatedRoadSection,
    updatedRoadSections,
    setColumnVisibility,
    filterUpdated,
} = referenceSlice.actions;
export const referenceReducer = referenceSlice.reducer;
